import { ReactNode } from "react";
import { CSSObject } from "@emotion/react";
import { Colors, Icon } from "main";
import {
  appStoreBadge,
  googlePlayBadge,
  loginHeroAdmin,
  loginHeroSub,
  swingLogoBlue,
  swingLogoWhite,
} from "swing-assets";
import {
  generateSwingReferURL,
  generateSwingRootURL,
  generateSwingSchoolsAppleAppStoreURL,
  generateSwingSchoolsGooglePlayStoreURL,
  generateSwingSubsAppleAppStoreURL,
  generateSwingSubsGooglePlayStoreURL,
} from "swing-utils";

import { translate } from "~components/string-table";

export type App = "swing-subs" | "swing-schools";

type AuthLayoutProps = {
  app: App;
  build?: string | null;
  children: ReactNode;
  isWeb?: boolean;
  onBackNav?: () => void;
  title?: string;
  version?: string | null;
};

export function AuthLayout(props: AuthLayoutProps) {
  const { app, build, children, isWeb, onBackNav, title, version } = props;
  const isSubsApp = app === "swing-subs";

  return (
    <>
      <div css={{ display: "flex", height: "100%" }}>
        {/* left column */}
        <div
          title={translate("LOGIN_LAYOUT_SPLASH_IMAGE_ALT_TEXT")}
          css={{
            display: "none",
            backgroundImage: `url(${isSubsApp ? loginHeroSub : loginHeroAdmin})`,
            backgroundSize: "cover",
            backgroundPosition: "top",
            "@media (min-width: 900px)": {
              display: "flex",
              width: "50%",
            },
          }}
        />
        {/* right column */}
        <div
          css={{
            display: "flex",
            width: "100%",
            padding: "var(--ion-safe-area-top, 0) 0 0 0",
            color: Colors.black,
            background: isSubsApp ? Colors.white200 : Colors.blue500,
            "@media (min-width: 900px)": {
              display: "block",
              width: "50%",
            },
          }}
        >
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              alignItems: "center",
              background: Colors.white300,
            }}
          >
            {/** header and background */}
            <div css={{ width: "100%" }}>
              <div
                css={
                  {
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    position: "relative",
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 80,
                    background: isSubsApp ? Colors.white200 : Colors.blue500,
                    overflow: "hidden",
                    "&:before": {
                      // used to carve out the bottom of the blue (admin-app) or white (sub-app) background
                      content: `""`,
                      position: "absolute",
                      bottom: -110,
                      left: "-10%",
                      width: "120%",
                      height: 190,
                      background: Colors.white300,
                      borderRadius: "50%",
                      boxShadow: isSubsApp ? "0px 0px 16px 4px rgba(40, 60, 93, 0.16)" : undefined,
                    },
                  } as CSSObject
                }
              >
                {onBackNav && (
                  <div
                    css={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      maxWidth: 360,
                      "@media (min-width: 900px)": {
                        maxWidth: "none",
                        right: "auto", // position back nav on left
                        left: 16,
                      },
                    }}
                  >
                    <BackNav isSubsApp={isSubsApp} onBack={onBackNav} />
                  </div>
                )}
                {/* header container */}
                <div
                  css={{
                    width: "100%",
                    maxWidth: 360,
                    padding: "64px 16px 0px",
                    paddingTop: 40,
                    paddingBottom: 40,
                    marginLeft: "auto",
                    marginRight: "auto",
                    "@media (min-width: 900px)": {
                      paddingTop: 80,
                    },
                  }}
                >
                  <img
                    src={isSubsApp ? swingLogoBlue : swingLogoWhite}
                    alt={translate("LOGIN_FORM_CONTAINER_SWING_LOGO_ALT_TEXT")}
                    css={{ width: 32, height: 32, marginRight: "auto", marginBottom: 16 }}
                  />
                  <h2
                    css={{
                      minHeight: 60, // attempt to keep header size consistent between screens
                      fontFamily: "var(--swing-font-family-headers)",
                      fontSize: 24,
                      fontWeight: 400,
                      margin: 0,
                      marginRight: "auto",
                      padding: 0,
                      ...(!isSubsApp && {
                        color: Colors.white200,
                      }),
                    }}
                  >
                    {/* Hides if title (an override) is set */}
                    {!title && (
                      <div css={{ fontSize: 12 }}>
                        {isSubsApp
                          ? translate("LOGIN_TITLE_SWING_SUBS_HEADER")
                          : translate("LOGIN_TITLE_SWING_SCHOOLS_HEADER")}
                      </div>
                    )}
                    <span>
                      {title
                        ? title
                        : isSubsApp
                          ? translate("LOGIN_TITLE_SWING_SUBS")
                          : app === "swing-schools"
                            ? translate("LOGIN_TITLE_SWING_SCHOOLS")
                            : translate("LOGIN_EMAIL_ENTRY_LOGIN_MESSAGE")}
                    </span>
                  </h2>
                </div>
              </div>
            </div>
            {/* Form + sign in via google buttons */}
            <div
              css={{
                width: "100%",
                height: "100%",
                display: "flex",
                position: "relative",
                zIndex: 3,
                flexDirection: "column",
                marginBottom: "auto",
                gap: 32,
                paddingLeft: 16,
                paddingRight: 16,
                marginTop: -16,
                "@media (min-width: 900px)": {
                  maxWidth: 360,
                  marginTop: -32,
                },
              }}
            >
              {children}
            </div>
            <div>
              <LoginFooter app={app} build={build} isWeb={isWeb} version={version} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const termsOfServiceLink = generateSwingRootURL("/terms");
const supportArticleLink = generateSwingReferURL("/tips-to-log-into-swing");

type LoginFooterProps = {
  app: App;
  build?: string | null;
  isWeb?: boolean;
  version?: string | null;
};

function LoginFooter(props: LoginFooterProps) {
  const { app, build, isWeb, version } = props;
  return (
    <footer
      css={{
        padding: "16px 16px calc(16px + var(--ion-safe-area-bottom, 0))",
        fontSize: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        "@media (min-width: 900px)": {
          fontSize: 12,
        },
      }}
    >
      {isWeb && <MobileDownloadLinks app={app} />}
      <div>
        <a href={termsOfServiceLink} target="_blank" rel="noreferrer">
          {translate("LOGIN_FOOTER_TERMS_OF_SERVICE")}
        </a>
        <span
          css={{
            color: Colors.slate400,
            margin: "0 6px",
            fontSize: "1.5em",
            lineHeight: "normal",
            display: "inline-block",
          }}
        >
          •
        </span>
        <a href={supportArticleLink} target="_blank" rel="noreferrer">
          {translate("LOGIN_FOOTER_SUPPORT")}
        </a>
      </div>
      <AppBuildVersionInfo version={version} build={build} margin="8px 0 0 0" />
    </footer>
  );
}

type BackNavProps = {
  isSubsApp: boolean;
  onBack: () => void;
};

function BackNav(props: BackNavProps) {
  const { isSubsApp, onBack } = props;

  return (
    <div>
      <button
        css={{
          display: "flex",
          alignItems: "center",
          background: "none",
        }}
        onClick={onBack}
        aria-label={translate("FORGOT_PASSWORD_RESET_BACK_TO_LOGIN_ARIA_MESSAGE")}
      >
        <Icon name="Arrow Left" color={isSubsApp ? Colors.blue500 : Colors.white200} />
        <span
          css={{
            cursor: "pointer",
            fontWeight: "var(--swing-font-medium)",
            color: isSubsApp ? "var(--ion-color-primary)" : Colors.white200,
          }}
        >
          {translate("FORGOT_PASSWORD_LABEL_BACK")}
        </span>
      </button>
    </div>
  );
}

type MobileDownloadLinksProps = {
  app: App;
};
function MobileDownloadLinks(props: MobileDownloadLinksProps) {
  const { app } = props;
  const isSubsApp = app === "swing-subs";

  const SWING_SUBS_APPLE_STORE_URL = generateSwingSubsAppleAppStoreURL();
  const SWING_SUBS_GOOGLE_PLAY_URL = generateSwingSubsGooglePlayStoreURL();

  const SWING_SCHOOLS_APPLE_STORE_URL = generateSwingSchoolsAppleAppStoreURL();
  const SWING_SCHOOLS_GOOGLE_PLAY_URL = generateSwingSchoolsGooglePlayStoreURL();

  return (
    <div css={{ display: "flex", gap: 16 }}>
      <a
        href={isSubsApp ? SWING_SUBS_APPLE_STORE_URL : SWING_SCHOOLS_APPLE_STORE_URL}
        target="_blank"
        rel="noreferrer"
      >
        <img src={appStoreBadge} alt={translate("APP_STORE_ALT_TEXT")} />
      </a>
      <a
        href={isSubsApp ? SWING_SUBS_GOOGLE_PLAY_URL : SWING_SCHOOLS_GOOGLE_PLAY_URL}
        target="_blank"
        rel="noreferrer"
      >
        <img src={googlePlayBadge} alt={translate("GOOGLE_PLAY_ALT_TEXT")} />
      </a>
    </div>
  );
}

type AppBuildVersionInfoProps = {
  version?: string | null;
  build?: string | null;
  margin?: CSSObject["margin"];
};
export function AppBuildVersionInfo(props: AppBuildVersionInfoProps) {
  const { version, build, margin } = props;

  if (!version && !build) {
    return null;
  }

  return (
    <div css={{ margin, fontSize: 12, color: Colors.slate400 }}>
      <span>Version {version}</span>
      <span> • </span>
      <span>Build {build}</span>
    </div>
  );
}
